import React, { useEffect, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";
import { FaTimesCircle } from "react-icons/fa";
import {
  MdOutlinePhoneAndroid,
  MdOutlineSportsEsports,
  MdFlashOn,
} from "react-icons/md";

import type { AdminBillPaymentTransaction } from "../../lib/api/adminTransactionsData";
import { formatAmount } from "../../utils/FormatPerson";
import { statusBadgeClass } from "../orders/ordersOrderTable";
import {
  getBillPaymentDisplayDate,
  getBillPaymentUtilityInfo,
} from "./billPaymentTransactionsTable";

type Props = {
  open: boolean;
  onClose: () => void;
  transaction: AdminBillPaymentTransaction | null;
};

function str(v: unknown): string {
  return typeof v === "string" ? v : "";
}

function num(v: unknown): number | undefined {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v);
    return Number.isNaN(n) ? undefined : n;
  }
  return undefined;
}

function boolLabel(v: unknown): string {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "—";
}

function formatFieldLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const UTILITY_DATA_KEY_ORDER: string[] = [
  "transaction_status",
  "status",
  "ref",
  "trans_id",
  "amount",
  "total_charge",
  "phone",
  "network",
  "product",
  "meter_number",
  "meter_type",
  "service",
  "customer_name",
  "token",
  "token_amount",
  "vendType",
  "serviceCategoryId",
  "created_at",
  "receiver",
  "processed",
];

function orderedUtilityKeys(data: Record<string, unknown>): string[] {
  const keys = Object.keys(data);
  const priority = UTILITY_DATA_KEY_ORDER.filter((k) => keys.includes(k));
  const rest = keys.filter((k) => !UTILITY_DATA_KEY_ORDER.includes(k)).sort();
  return [...priority, ...rest];
}

function formatUtilityValue(value: unknown): React.ReactNode {
  if (value == null) return <span className="text-[#617889]">—</span>;
  if (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  ) {
    const s = String(value);
    if (s.length > 280) {
      return (
        <span className="break-words font-mono text-[12px] leading-relaxed text-[#C6D7E4]">
          {s.slice(0, 280)}…
        </span>
      );
    }
    return (
      <span className="break-words text-[13px] leading-relaxed text-[#EEF2F6]">
        {s}
      </span>
    );
  }
  try {
    const json = JSON.stringify(value, null, 2);
    const clipped = json.length > 4000 ? `${json.slice(0, 4000)}\n…` : json;
    return (
      <pre className="max-h-56 overflow-auto rounded-lg border border-[#2C363E] bg-[#0D1114] p-3 text-left font-mono text-[11px] leading-snug text-[#B8C5CE]">
        {clipped}
      </pre>
    );
  } catch {
    return String(value);
  }
}

function SectionCard({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-xl border border-[#2C363E] bg-[#161C22]/55 p-4 shadow-[inset_0_1px_0_0_rgba(255,255,255,0.035)] sm:p-5">
      <h3 className="mb-4 flex items-center gap-2.5 text-[11px] font-bold uppercase tracking-[0.18em] text-[#869AA9]">
        <span
          className="h-px w-7 shrink-0 bg-gradient-to-r from-[#4FB0FF]/90 to-transparent"
          aria-hidden
        />
        {title}
      </h3>
      <div className="divide-y divide-[#23292F]">{children}</div>
    </section>
  );
}

function KV({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 py-3.5 first:pt-0 last:pb-0 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
      <dt className="min-w-[7.5rem] shrink-0 text-[12px] font-medium text-[#7D8F9C]">
        {label}
      </dt>
      <dd className="min-w-0 flex-1 text-left text-[13px] font-medium leading-snug text-[#EEF2F6] [overflow-wrap:anywhere] sm:text-right">
        {value}
      </dd>
    </div>
  );
}

function CopyChip({ text, label }: { text: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const onCopy = async () => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1600);
    } catch {
      toast.error("Could not copy");
    }
  };

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        void onCopy();
      }}
      className="inline-flex max-w-full items-center gap-2 rounded-lg border border-[#3A4852]/80 bg-[#0D1114]/80 px-2.5 py-1.5 text-left transition hover:border-[#4FB0FF]/40 hover:bg-[#1C2227]"
    >
      <span className="min-w-0 truncate font-mono text-[11px] leading-snug text-[#C6D7E4]">
        {text}
      </span>
      <span className="shrink-0 rounded border border-[#3A4852] px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-[#AEBCC6]">
        {copied ? "Done" : "Copy"}
      </span>
    </button>
  );
}

function SubtypeIcon({ typeRaw }: { typeRaw: string }) {
  const u = typeRaw.toUpperCase();
  if (u === "ELECTRICITY") {
    return <MdFlashOn className="h-6 w-6 text-amber-300/90" aria-hidden />;
  }
  if (u === "BETTING") {
    return (
      <MdOutlineSportsEsports
        className="h-6 w-6 text-emerald-300/90"
        aria-hidden
      />
    );
  }
  return <MdOutlinePhoneAndroid className="h-6 w-6 text-[#4FB0FF]" aria-hidden />;
}

const AdminBillPaymentDetailModal = ({ open, onClose, transaction }: Props) => {
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const id = window.requestAnimationFrame(() =>
      closeRef.current?.focus()
    );
    return () => window.cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const utilityRows = useMemo(() => {
    if (!transaction) return [];
    const { data } = getBillPaymentUtilityInfo(transaction);
    if (!data) return [];
    return orderedUtilityKeys(data).map((key) => ({
      key,
      label: formatFieldLabel(key),
      value: formatUtilityValue(data[key]),
    }));
  }, [transaction]);

  if (!open || !transaction) return null;

  const tx = transaction;
  const statusUpper = str(tx.status).toUpperCase() || "—";
  const idShort =
    tx._id.length > 14 ? `${tx._id.slice(0, 8)}…${tx._id.slice(-4)}` : tx._id;

  const { typeRaw, typeLabel, data: utilityDataObj } = getBillPaymentUtilityInfo(tx);
  const usdt = num(tx.usdtAmount) ?? num(tx.amount);
  const ngn = num(tx.amountNGN);
  const fees = num(tx.fees);

  const u = tx.user;
  let displayName = "—";
  let email = "";
  let userMongo = "";
  if (u && typeof u === "object") {
    const profile = (
      u as { profile?: { firstName?: string; lastName?: string } }
    ).profile;
    const f = profile?.firstName?.trim() ?? "";
    const l = profile?.lastName?.trim() ?? "";
    displayName = `${f} ${l}`.trim() || "—";
    email = (u as { email?: { address?: string } }).email?.address?.trim() ?? "";
    userMongo = str((u as { _id?: unknown })._id);
  }

  const created = getBillPaymentDisplayDate(tx);
  const updatedIso = str(tx.updatedAt);
  let updatedLabel = "—";
  if (updatedIso) {
    try {
      updatedLabel = new Date(updatedIso).toLocaleString(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      });
    } catch {
      updatedLabel = updatedIso;
    }
  }

  return (
    <div className="fixed inset-0 z-[150] flex items-end justify-center p-0 font-manrope sm:items-center sm:p-6">
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
        onClick={onClose}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="billpay-detail-title"
        className="relative z-[151] flex max-h-[92vh] w-full max-w-xl min-w-0 flex-col overflow-hidden rounded-t-2xl border border-[#2C363E] bg-[#13181B] shadow-[0_-12px_48px_-16px_rgba(0,0,0,0.88)] sm:max-h-[90vh] sm:rounded-2xl sm:shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="shrink-0 border-b border-[#2C363E]/90 bg-gradient-to-br from-[#1a222d] via-[#141a20] to-[#13181B] px-4 pb-4 pt-4 sm:px-6 sm:pt-5">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span
                  className={`inline-flex rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${statusBadgeClass(
                    tx.status
                  )}`}
                >
                  {statusUpper}
                </span>
                {typeRaw ? (
                  <span className="inline-flex rounded-full border border-[#3A4852] bg-[#1C2227]/90 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-[#AEBCC6]">
                    {typeRaw}
                  </span>
                ) : null}
              </div>
              <h2
                id="billpay-detail-title"
                className="font-sora text-lg font-bold leading-snug text-white sm:text-xl"
              >
                Bill payment
                <span className="font-normal text-[#5C6B76]"> · </span>
                <span className="font-mono text-[15px] font-semibold text-[#C6D7E4]">
                  {idShort}
                </span>
              </h2>
              <p className="text-[12px] text-[#869AA9]">
                {typeLabel !== "—" ? (
                  <span className="font-semibold text-[#BEE1FB]">{typeLabel}</span>
                ) : (
                  <span>Utility</span>
                )}
                <span className="text-[#5C6B76]"> · </span>
                {created}
              </p>
              <div className="flex flex-col gap-2 pt-1 sm:flex-row sm:flex-wrap sm:items-center">
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#617889]">
                  Transaction ID
                </span>
                <CopyChip text={tx._id} label="Transaction ID" />
              </div>
            </div>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              className="shrink-0 text-white"
              aria-label="Close"
            >
              <FaTimesCircle className="text-2xl" />
            </button>
          </div>
        </header>

        <div className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden px-4 py-5 scrollbar-thin scrollbar-track-[#13181B] scrollbar-thumb-[#3A4852] sm:px-6">
          <div className="min-w-0 space-y-5">
            <div className="grid min-w-0 grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E] bg-gradient-to-b from-[#1C2227] to-[#161E25] p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Amount (NGN)
                </p>
                <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-white [overflow-wrap:anywhere] sm:text-2xl">
                  {ngn != null ? `₦${formatAmount(ngn)}` : "—"}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  USDT
                </p>
                <p className="mt-2 min-w-0 font-sora text-xl font-bold tabular-nums leading-snug text-[#BEE1FB] [overflow-wrap:anywhere] sm:text-2xl">
                  {usdt != null ? `${formatAmount(usdt)}` : "—"}
                </p>
              </div>
              <div className="min-w-0 overflow-hidden rounded-xl border border-[#2C363E]/80 bg-[#161E25]/60 p-4">
                <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                  Fees
                </p>
                <p className="mt-2 font-sora text-xl font-semibold tabular-nums text-[#E7EBEE] sm:text-2xl">
                  {fees != null ? formatAmount(fees) : "—"}
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-[#2C363E] bg-gradient-to-br from-[#1a2430]/90 via-[#151c24] to-[#13181B] p-4 sm:p-5">
              <div className="flex items-start gap-3">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl border border-[#3A4852]/80 bg-[#0D1114]/80">
                  <SubtypeIcon typeRaw={typeRaw} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-[10px] font-bold uppercase tracking-wider text-[#617889]">
                    Bill category
                  </p>
                  <p className="mt-1 text-[18px] font-bold leading-snug text-white">
                    {typeLabel}
                  </p>
                  <p className="mt-2 text-[12px] text-[#8FA3AF]">
                    Currency{" "}
                    <span className="font-semibold text-[#E7EBEE]">
                      {str(tx.currency) || "—"}
                    </span>
                    <span className="mx-2 text-[#5C6B76]">·</span>
                    Pending{" "}
                    <span className="font-semibold text-[#E7EBEE]">
                      {boolLabel(tx.isPending)}
                    </span>
                    <span className="mx-2 text-[#5C6B76]">·</span>
                    Balance deducted{" "}
                    <span className="font-semibold text-[#E7EBEE]">
                      {boolLabel(tx.balanceDeducted)}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <SectionCard title="User">
              <KV label="Name" value={displayName} />
              {email ? <KV label="Email" value={email} /> : null}
              <KV
                label="User ID (trade)"
                value={
                  str(tx.userId) ? (
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                      <span className="break-all font-mono text-[12px] text-[#C6D7E4]">
                        {str(tx.userId)}
                      </span>
                      <CopyChip text={str(tx.userId)} label="User ID" />
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              {userMongo && userMongo !== str(tx.userId) ? (
                <KV
                  label="User _id (embedded)"
                  value={
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                      <span className="break-all font-mono text-[12px] text-[#C6D7E4]">
                        {userMongo}
                      </span>
                      <CopyChip text={userMongo} label="User _id" />
                    </div>
                  }
                />
              ) : null}
            </SectionCard>

            <SectionCard title="Identifiers">
              <KV
                label="Withdrawal ID"
                value={
                  str(tx.withdrawalId) ? (
                    <div className="flex flex-col items-stretch gap-2 sm:items-end">
                      <span className="break-all font-mono text-[12px] text-[#C6D7E4]">
                        {str(tx.withdrawalId)}
                      </span>
                      <CopyChip
                        text={str(tx.withdrawalId)}
                        label="Withdrawal ID"
                      />
                    </div>
                  ) : (
                    "—"
                  )
                }
              />
              <KV label="Created" value={created} />
              <KV label="Updated" value={updatedLabel} />
              {tx.__v != null ? (
                <KV label="Version (__v)" value={String(tx.__v)} />
              ) : null}
            </SectionCard>

            <SectionCard title="Provider payload">
              {!utilityDataObj ? (
                <p className="py-4 text-center text-[13px] text-[#617889]">
                  No utility payload on this record.
                </p>
              ) : (
                utilityRows.map(({ key, label, value }) => (
                  <KV key={key} label={label} value={value} />
                ))
              )}
            </SectionCard>
          </div>
        </div>

        <div className="shrink-0 border-t border-[#2C363E] bg-[#13181B] px-4 py-3 sm:px-6">
          <button
            type="button"
            onClick={onClose}
            className="flex h-11 w-full items-center justify-center rounded-xl border border-[#3A4852] text-[14px] font-bold text-[#E7EBEE] transition hover:bg-[#1C2227]"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminBillPaymentDetailModal;
